// Mantra integration layer — talks ONLY to official local Mantra services.
// 1) MFS100 Client Service (non-RD): custom HTTP verbs INFO/CAPTURE/MATCH/VERIFY on /mfs100/*
// 2) RD Service (MFS110 L1): RDSERVICE/DEVICEINFO/CAPTURE on localhost:11100-11120 (encrypted PID only)

export const SCANNER_STATE = {
  CONNECTED: "Scanner Connected",
  DISCONNECTED: "Scanner Disconnected",
  NOT_FOUND: "Scanner Not Found",
  RD_UNAVAILABLE: "RD Service Not Available",
  DRIVER_MISSING: "Driver Not Installed",
  BUSY: "Device Busy",
  READY: "Capture Ready",
  CAPTURING: "Capturing",
  SUCCESS: "Capture Successful",
  FAILED: "Capture Failed",
};

async function localRequest(url, method, body, timeoutMs = 4000) {
  const ctrl = new AbortController();
  const t = setTimeout(() => ctrl.abort(), timeoutMs);
  try {
    const res = await fetch(url, { method, body, signal: ctrl.signal, headers: body ? { "Content-Type": "text/plain" } : undefined });
    return await res.text();
  } finally {
    clearTimeout(t);
  }
}

// ---------- MFS100 Client Service ----------
// Newer MFS100ClientService accepts standard GET/POST JSON; older builds (mfs100.js) use custom verbs INFO/CAPTURE/VERIFY/MATCH.
async function clientCall(base, path, verb, body, timeoutMs) {
  const url = `${base}${path}`;
  const attempts = body == null ? [["GET", null], [verb, null]] : [["POST", body], [verb, body]];
  let lastErr;
  for (const [method, payload] of attempts) {
    try {
      const txt = await localRequest(url, method, payload, timeoutMs);
      const data = JSON.parse(txt);
      if (data && typeof data === "object") return data;
    } catch (e) { lastErr = e; }
  }
  throw lastErr || new Error("Client service unreachable");
}

export async function mfs100Info(urls) {
  for (const base of urls) {
    try {
      const data = await clientCall(base, "info", "INFO", null, 2500);
      return { base, ...data };
    } catch { /* try next */ }
  }
  return null;
}

export const mfs100Capture = (base, quality, timeout) =>
  clientCall(base, "capture", "CAPTURE", JSON.stringify({ Quality: quality, TimeOut: timeout }), (timeout + 5) * 1000);

export const mfs100Verify = (base, probeTemplate, galleryTemplate) =>
  clientCall(base, "verify", "VERIFY", JSON.stringify({ ProbTemplate: probeTemplate, GalleryTemplate: galleryTemplate, BioType: "FMR" }), 15000);

export const mfs100Match = (base, galleryTemplate, quality, timeout) =>
  clientCall(base, "match", "MATCH", JSON.stringify({ Quality: quality, TimeOut: timeout, GalleryTemplate: galleryTemplate, BioType: "FMR" }), (timeout + 5) * 1000);

// ---------- RD Service (MFS110 L1) ----------
function attr(xml, name) {
  const m = xml.match(new RegExp(`${name}="([^"]*)"`));
  return m ? m[1] : null;
}

export async function rdDiscover(portStart, portEnd) {
  for (let port = portStart; port <= portEnd; port++) {
    for (const proto of ["http", "https"]) {
      try {
        const xml = await localRequest(`${proto}://127.0.0.1:${port}/`, "RDSERVICE", null, 1500);
        if (xml.includes("RDService")) {
          const info = attr(xml, "info") || "";
          if (!/mantra/i.test(info)) continue;
          const capturePath = (xml.match(/id="CAPTURE"\s+path="([^"]+)"/) || [])[1] || "/rd/capture";
          const infoPath = (xml.match(/id="DEVICEINFO"\s+path="([^"]+)"/) || [])[1] || "/rd/info";
          return { base: `${proto}://127.0.0.1:${port}`, status: attr(xml, "status"), info, capturePath, infoPath };
        }
      } catch { /* next */ }
    }
  }
  return null;
}

export async function rdDeviceInfo(rd) {
  const xml = await localRequest(`${rd.base}${rd.infoPath}`, "DEVICEINFO", null, 4000);
  return { raw: xml, dpId: attr(xml, "dpId"), rdsId: attr(xml, "rdsId"), rdsVer: attr(xml, "rdsVer"), dc: attr(xml, "dc"), mi: attr(xml, "mi"), mc: attr(xml, "mc") };
}

export async function rdCapture(rd, timeoutSec) {
  const pidOptions = `<?xml version="1.0"?><PidOptions ver="1.0"><Opts fCount="1" fType="2" iCount="0" pCount="0" format="0" pidVer="2.0" timeout="${timeoutSec * 1000}" posh="UNKNOWN" env="P" /></PidOptions>`;
  const xml = await localRequest(`${rd.base}${rd.capturePath}`, "CAPTURE", pidOptions, (timeoutSec + 5) * 1000);
  return { raw: xml, errCode: attr(xml, "errCode"), errInfo: attr(xml, "errInfo"), qScore: attr(xml, "qScore"), fCount: attr(xml, "fCount"), nmPoints: attr(xml, "nmPoints") };
}

// ---------- Combined detection ----------
export async function detectScanner(settings) {
  const result = { mode: null, state: SCANNER_STATE.NOT_FOUND, client: null, rd: null, rdInfo: null, driver: false, service: false, rdService: false, message: "" };
  const client = await mfs100Info(settings.client_service_urls || []);
  if (client) {
    result.service = true;
    result.client = client;
    const devicePresent = (client.ErrorCode === "0" || client.ErrorCode === 0) || (!("ErrorCode" in client) && (client.SerialNo || client.Model));
    if (devicePresent) {
      result.mode = "mfs100_client"; result.driver = true;
      result.state = SCANNER_STATE.CONNECTED;
      result.message = `${client.Make || "Mantra"} ${client.Model || ""} · S/N ${client.SerialNo || "—"}`;
    } else {
      result.state = /busy/i.test(client.ErrorDescription || "") ? SCANNER_STATE.BUSY : SCANNER_STATE.DISCONNECTED;
      result.message = client.ErrorDescription || "Client service running but device not detected";
    }
  }
  const rd = await rdDiscover(settings.rd_port_start || 11100, settings.rd_port_end || 11120);
  if (rd) {
    result.rd = rd; result.rdService = true;
    try {
      const info = await rdDeviceInfo(rd);
      result.rdInfo = info;
      if (info.dc && rd.status === "READY") {
        result.driver = true;
        if (!result.mode) {
          result.mode = "rd_service"; result.state = SCANNER_STATE.CONNECTED;
          result.message = `RD Service READY · ${rd.info} · Device ${info.mi || ""} (${info.dc})`;
        }
      } else if (!result.mode) {
        result.state = rd.status === "NOTREADY" ? SCANNER_STATE.DISCONNECTED : SCANNER_STATE.NOT_FOUND;
        result.message = `RD Service status ${rd.status || "unknown"} — device not ready`;
      }
    } catch (e) {
      if (!result.mode) { result.state = SCANNER_STATE.DISCONNECTED; result.message = "RD Service found but device info failed"; }
    }
  }
  if (!client && !rd) {
    result.state = SCANNER_STATE.RD_UNAVAILABLE;
    result.message = "No Mantra service found on this computer. Install Mantra MFS110 RD Service / MFS100 Client Service and connect the device.";
  } else if (!result.driver && result.state === SCANNER_STATE.NOT_FOUND) {
    result.state = SCANNER_STATE.DRIVER_MISSING;
  }
  return result;
}

export const MFS100_ERRORS = {
  "-1": "Device not connected", "-1140": "Capture timed out — finger not detected", "-1132": "Device not connected",
  "-1133": "Finger not detected / capture timed out", "-1137": "Poor quality fingerprint — please recapture",
  "-1134": "Capture failed", "-1141": "Capture cancelled", "-1307": "Device busy",
};
